import { useState } from 'react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { Alert, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import type { NormalizedApiError } from '@/shared/api';
import { SettingsCard } from '@/features/store-settings';

import { distinctLocales, requiredTextsSummary } from '../../model/availability';
import type { RequiredTextRow } from '../../model/availability';
import { templateLabelKeys } from '../../model/templateLabels';
import type {
  ChatbotAvailability,
  ChatbotTemplateResolution,
  ContentLanguage,
} from '../../model/types';

export interface RequiredTextsCardProps {
  query: UseQueryResult<ChatbotAvailability, NormalizedApiError>;
  canViewTemplates: boolean;
  /** The tenant's content-language registry (design §17.4: "language name + code … unknown → the
   * code") — also gives the secondary sort its "tenant's default language first" ordering. */
  languages: ContentLanguage[] | undefined;
}

const STATUS_CHIP: Record<
  ChatbotTemplateResolution,
  { color: 'warning' | 'info' | 'default'; variant: 'filled' | 'outlined' }
> = {
  EMPTY: { color: 'warning', variant: 'filled' },
  SYSTEM_DEFAULT: { color: 'info', variant: 'outlined' },
  ACCEPTED_DEFAULT: { color: 'default', variant: 'outlined' },
  TENANT: { color: 'default', variant: 'outlined' },
};

function RequiredTextRowView({
  row,
  canViewTemplates,
  languages,
}: {
  row: RequiredTextRow;
  canViewTemplates: boolean;
  languages: ContentLanguage[] | undefined;
}) {
  const { t } = useTranslation('chatbot');
  const { labelKey } = templateLabelKeys(row.key);
  const label = t(labelKey, row.key);
  const chip = STATUS_CHIP[row.resolution];
  const linkLabel =
    row.resolution === 'EMPTY' ? t('textsLink.required.write') : t('textsLink.required.customise');
  // Design §17.4: "language name + code (from `useContentLanguages`; unknown → the code)".
  const language = languages?.find((l) => l.locale === row.locale);
  const languageLabel = language ? `${language.name} (${row.locale})` : row.locale;

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75, flexWrap: 'wrap' }}>
      <Chip
        size="small"
        color={chip.color}
        variant={chip.variant}
        label={t(`textsLink.required.status.${row.resolution}`)}
      />
      <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" noWrap>
          {label}{' '}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {row.key}
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {languageLabel} · {row.channels.join(', ')}
        </Typography>
      </Stack>
      {canViewTemplates ? (
        <Button
          size="small"
          component={RouterLink}
          to={`/settings/chatbot/templates?key=${encodeURIComponent(row.key)}&locale=${encodeURIComponent(row.locale)}`}
        >
          {linkLabel}
        </Button>
      ) : null}
    </Stack>
  );
}

/**
 * The required bot texts card (design §17.4, closing hub plan-conversation-004 Phase 2.5) — answers
 * "is the bot silent because a text is missing?" next to the availability strip's other switches.
 * Visible to every `settings.chatbot.view` holder even when `botConfiguredKnown=false` (required-text
 * completeness comes from the content projection, not the bot registry).
 */
export function RequiredTextsCard({ query, canViewTemplates, languages }: RequiredTextsCardProps) {
  const { t } = useTranslation('chatbot');
  const [showAll, setShowAll] = useState(false);
  const defaultLocale = languages?.find((l) => l.defaultLocale)?.locale;

  return (
    <SettingsCard
      title={t('textsLink.title')}
      description={t('textsLink.description')}
      icon={<ForumRoundedIcon />}
      divided={false}
      action={
        canViewTemplates ? (
          <Button
            size="small"
            variant="outlined"
            component={RouterLink}
            to="/settings/chatbot/templates"
          >
            {t('textsLink.open')}
          </Button>
        ) : undefined
      }
    >
      {query.isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
        </Stack>
      ) : query.isError ? null : query.data ? (
        (() => {
          const summary = requiredTextsSummary(query.data, defaultLocale);
          if (summary.unknown) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t('textsLink.required.unknown')}
              </Typography>
            );
          }
          const configured = [...summary.fine];
          // Design §17.4: "the distinct locales, named" — language names, not raw codes; unknown →
          // the code (same fallback as each row's own language label).
          const allSetLanguages = distinctLocales(summary.rows)
            .map((locale) => languages?.find((l) => l.locale === locale)?.name ?? locale)
            .join(', ');
          return (
            <Stack spacing={1.5}>
              {summary.missing.length > 0 ? (
                <Stack spacing={0.5}>
                  <Alert severity="warning" role="status">
                    {t(
                      summary.missing.length === 1
                        ? 'textsLink.required.missing_one'
                        : 'textsLink.required.missing_other',
                      { count: summary.missing.length },
                    )}
                  </Alert>
                  {summary.missing.map((row) => (
                    <RequiredTextRowView
                      key={`${row.key}:${row.locale}:${row.resolution}`}
                      row={row}
                      canViewTemplates={canViewTemplates}
                      languages={languages}
                    />
                  ))}
                </Stack>
              ) : null}
              {summary.platformWording.length > 0 ? (
                <Stack spacing={0.5}>
                  <Alert severity="info" role="status">
                    {t(
                      summary.platformWording.length === 1
                        ? 'textsLink.required.systemDefault_one'
                        : 'textsLink.required.systemDefault_other',
                      { count: summary.platformWording.length },
                    )}
                  </Alert>
                  {summary.platformWording.map((row) => (
                    <RequiredTextRowView
                      key={`${row.key}:${row.locale}:${row.resolution}`}
                      row={row}
                      canViewTemplates={canViewTemplates}
                      languages={languages}
                    />
                  ))}
                </Stack>
              ) : null}
              {summary.missing.length === 0 && summary.platformWording.length === 0 ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleRoundedIcon color="success" fontSize="small" />
                  <Typography variant="body2">
                    {t('textsLink.required.allSet', { languages: allSetLanguages })}
                  </Typography>
                </Stack>
              ) : null}
              {configured.length > 0 ? (
                <>
                  <Button
                    size="small"
                    onClick={() => setShowAll((v) => !v)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {showAll
                      ? t('textsLink.required.hideAll')
                      : t('textsLink.required.showAll', { count: configured.length })}
                  </Button>
                  {showAll
                    ? configured.map((row) => (
                        <RequiredTextRowView
                          key={`${row.key}:${row.locale}:${row.resolution}`}
                          row={row}
                          canViewTemplates={canViewTemplates}
                          languages={languages}
                        />
                      ))
                    : null}
                </>
              ) : null}
              {!canViewTemplates ? (
                <Typography variant="caption" color="text.secondary">
                  {t('textsLink.required.noAccess')}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('textsLink.required.eventualHint')}
                </Typography>
              )}
            </Stack>
          );
        })()
      ) : null}
    </SettingsCard>
  );
}
